import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Archivo } from "@models/archivo.model";
import IEstadoDatatable from "@models/datatable.interface";
import IExpediente from "@models/expediente.interface";
import { Expediente } from "@models/expediente.model";
import { Licencia } from "@models/licencia.model";
import IMotivo from "@models/motivo.interface";
import { Prestador } from "@models/prestador.model";
import { TipoDocumento } from "@models/tipo-documento.model";
import { Trabajador } from "@models/trabajador.model";
import Funcionalidades from "@shared/enums/funcionalidades.enum";
import { assignToModel } from "@shared/utils/fieldsMap.utils";

@Injectable({ providedIn: "root" })
export class StateService {
	private prestador: Prestador;
	private expediente: Expediente;
	private licencia: Licencia;
	private tipoDocumento: TipoDocumento;
	private documento: Archivo;
	private estadoDatatableExpediente: IEstadoDatatable;
	private estadoDatatableLicencia: IEstadoDatatable;
	private conContraste: boolean;
    private renderer: Renderer2;
    private htmlElement: HTMLElement;
    private fontSizeLevel: 0 | 1 | 2;
    private funcionalidad: Funcionalidades;

    constructor(
         rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
        this.htmlElement = this.document.documentElement; 
    }

	setPrestador(p: Prestador) {
		this.prestador = p;
		this.actualizarStatePrestador();
	}

	getPrestador(): Prestador {
		if (!this.prestador && history.state?.prestador) {
			this.setPrestador(
				assignToModel(Prestador, history.state?.prestador)
			);
		}
		if (this.prestador && !history.state?.prestador) this.actualizarStatePrestador();
		return this.prestador;
	}

	updateEmail(email: string) {
		if (this.prestador) {
			this.prestador.email_fiscalizacion = email;
			this.actualizarStatePrestador();
		}
	}

	updateDiasHabilesExpediente(diasHabiles: number) {
		if (this.expediente) {
			this.expediente.dias_habiles = diasHabiles;
			this.actualizarStateExpediente();
		}
	}

	setExpediente(e: Expediente) {
		this.expediente = e;
		this.actualizarStateExpediente();
	}

	getExpediente(): Expediente {
		if (!this.expediente && history.state?.expediente) {
			const expediente: IExpediente = history.state?.expediente;
			expediente.prestador = assignToModel(
				Prestador,
				expediente.prestador
			);
			const motivo = {
				nombre: expediente.motivo.nombre,
				archivo: assignToModel(Archivo, expediente.motivo.archivo),
			} as IMotivo;
            expediente.motivo = motivo;
			this.setExpediente(assignToModel(Expediente, expediente));
		}
		if (this.expediente && !history.state?.expediente)
			this.actualizarStateExpediente();
		return this.expediente;
	}

	setLicencia(l: Licencia) {
		this.licencia = l;
		this.actualizarStateLicencia();
	}

	getLicencia(): Licencia {
		if (!this.licencia && history.state?.licencia) {
      const trabajador = assignToModel(Trabajador, history.state?.licencia.trabajador)
      const licencia = history.state?.licencia;
      licencia.trabajador = trabajador;
			this.setLicencia(assignToModel(Licencia, licencia));
		}
		if (this.licencia && !history.state?.licencia)
			this.actualizarStateLicencia();
		return this.licencia;
	}

	setTipoDocumento(td: TipoDocumento) {
		this.tipoDocumento = td;
		this.actualizarStateTipoDocumento();
	}

	getTipoDocumento(): TipoDocumento {
		if (!this.tipoDocumento && history.state?.tipoDocumento) {
			this.setTipoDocumento(assignToModel(TipoDocumento, history.state?.tipoDocumento));
		}
		if (this.tipoDocumento && !history.state?.tipoDocumento)
			this.actualizarStateTipoDocumento();
		return this.tipoDocumento;
	}

	setDocumento(d: Archivo) {
		this.documento = d;
		this.actualizarStateDocumento();
	}

	getDocumento(): Archivo {
		if (!this.documento && history.state?.documento) {
			this.setDocumento(assignToModel(Archivo, history.state?.documento));
		}
		if (this.documento && !history.state?.documento)
			this.actualizarStateDocumento();
		return this.documento;
	}

    setFuncionalidad(idFuncionalidad: Funcionalidades) {
		if (!this.funcionalidad && history.state?.funcionalidad) {
			this.funcionalidad =history.state?.funcionalidad;
		}
		if (this.funcionalidad && !history.state?.funcionalidad)
			this.actualizarStateFuncionalidad();
        if (this.funcionalidad !== idFuncionalidad) {
            this.setEstadoDatatableExpediente({page: 0, search: "", sort: ""})
            this.setEstadoDatatableLicencia({page: 0, search: "", sort: ""})
            this.funcionalidad = idFuncionalidad;
            this.actualizarStateFuncionalidad();
        }
    }

	setEstadoDatatableExpediente(e: IEstadoDatatable) {
		this.estadoDatatableExpediente = e;
		this.actualizarStateDatatableExpediente();
	}

	getEstadoDatatableExpediente(): IEstadoDatatable {
		if (
			!this.estadoDatatableExpediente &&
			history.state?.estadoDatatableExpediente
		) {
			this.setEstadoDatatableExpediente(
				history.state?.estadoDatatableExpediente
			);
		}
		if (
			this.estadoDatatableExpediente &&
			!history.state?.estadoDatatableExpediente
		)
			this.actualizarStateDatatableExpediente();
		return this.estadoDatatableExpediente;
	}

	setEstadoDatatableLicencia(e: IEstadoDatatable) {
		this.estadoDatatableLicencia = e;
		this.actualizarStateDatatableLicencia();
	}

	getEstadoDatatableLicencia(): IEstadoDatatable {
		if (
			!this.estadoDatatableLicencia &&
			history.state?.estadoDatatableLicencia
		) {
			this.setEstadoDatatableLicencia(
				history.state?.estadoDatatableLicencia
			);
		}
		if (
			this.estadoDatatableLicencia &&
			!history.state?.estadoDatatableLicencia
		)
			this.actualizarStateDatatableLicencia();
		return this.estadoDatatableLicencia;
	}

	setContraste(conContraste: boolean) {
		this.conContraste = conContraste;
   
        if (this.conContraste) this.renderer.addClass(this.htmlElement, "a11y-contrast");
        else this.renderer.removeClass(this.htmlElement, 'a11y-contrast');

		this.actualizarStateContraste();
	}

	getContraste(): void {
		if (typeof this.conContraste !== "boolean" && typeof history.state?.conContraste === "boolean") {
			this.setContraste(history.state?.conContraste);
		}
		if (typeof this.conContraste === "boolean" && typeof history.state?.conContraste !== "boolean")
			this.actualizarStateContraste();
  
        if (this.conContraste) this.renderer.addClass(this.htmlElement, "a11y-contrast");
	}

	setFontSize(size: 0 | 1 | 2): void {
		this.fontSizeLevel = size;
        this.updateFontSizeClass();
		this.actualizarStateFontSize();
	}

	getFontSize(): 0 | 1 | 2 {
		if (![0, 1, 2].includes(this.fontSizeLevel) && [0, 1, 2].includes(history.state?.fontSizeLevel)) {
			this.setFontSize(history.state?.fontSizeLevel);
		}
		if ([0, 1, 2].includes(this.fontSizeLevel) && ![0, 1, 2].includes(history.state?.fontSizeLevel))
			this.actualizarStateFontSize();
  
        this.updateFontSizeClass();

        return this.fontSizeLevel??0;
	}

	private actualizarStatePrestador() {
		history.replaceState(
			{ ...history.state, prestador: this.prestador },
			""
		);
	}

	private actualizarStateExpediente() {
		history.replaceState(
			{ ...history.state, expediente: this.expediente },
			""
		);
	}

	private actualizarStateLicencia() {
		history.replaceState({ ...history.state, licencia: this.licencia }, "");
	}

	private actualizarStateTipoDocumento() {
		history.replaceState({ ...history.state, tipoDocumento: this.tipoDocumento }, "");
	}

	private actualizarStateDocumento() {
		history.replaceState({ ...history.state, documento: this.documento }, "");
	}

	private actualizarStateFuncionalidad() {
		history.replaceState({ ...history.state, funcionalidad: this.funcionalidad }, "");
	}

	private actualizarStateDatatableExpediente() {
		history.replaceState(
			{
				...history.state,
				estadoDatatableExpediente: this.estadoDatatableExpediente,
			},
			""
		);
	}

	private actualizarStateDatatableLicencia() {
		history.replaceState(
			{
				...history.state,
				estadoDatatableLicencia: this.estadoDatatableLicencia,
			},
			""
		);
	}

	private actualizarStateContraste() {
		history.replaceState(
			{ ...history.state, conContraste: this.conContraste },
			""
		);
	}

	private actualizarStateFontSize() {
		history.replaceState(
			{ ...history.state, fontSizeLevel: this.fontSizeLevel },
			""
		);
	}

    private updateFontSizeClass(): void {
        this.renderer.removeClass(this.htmlElement, "a11y-font-0");
        this.renderer.removeClass(this.htmlElement, "a11y-font-1");
        this.renderer.removeClass(this.htmlElement, "a11y-font-2");

        switch (this.fontSizeLevel) {
            case 0:
                this.renderer.addClass(this.htmlElement, "a11y-font-0");
                break;
            case 1:
                this.renderer.addClass(this.htmlElement, "a11y-font-1");
                break;
            case 2:
                this.renderer.addClass(this.htmlElement, "a11y-font-2");
                break;
        }
    }
}
